import styles from "../../pages/BlogArticle.module.scss";
import { getSectionId, getShopAnchorId } from "./articleUtils";
import { BlogMedia, getInstagramUrl } from "./InstagramEmbed";
import { useLanguage } from "../../contexts/LanguageContext";
import { blogText } from "../../i18n/blog";
import { getLocalizedValue } from "../../utils/localizedContent";
import { getFacilityIcon } from "../../utils/facilityIcons";
import { trackEvent } from "../../utils/analytics";

const getMapQuery = (shop) => {
  return [shop.name, shop.area, "宮古島"].filter(Boolean).join(" ");
};

const getMapEmbedUrl = (shop) => {
  if (shop.mapEmbedUrl) {
    return shop.mapEmbedUrl;
  }

  return `https://www.google.com/maps?q=${encodeURIComponent(
    getMapQuery(shop),
  )}&output=embed`;
};

const getScore = (score) => {
  const number = Number(score);

  if (!number) return 0;

  return Math.min(Math.max(Math.round(number), 0), 5);
};

const getStars = (score) => {
  if (!score) return "";

  return "★".repeat(score);
};

const getRecommendedMenu = (shop) => {
  return (
    shop.recommendedMenu ||
    shop.recommendMenu ||
    shop.menu ||
    shop.orderRecommendation ||
    ""
  );
};

const getBusinessHours = (shop) => {
  return shop.businessHours || shop.openingHours || shop.hours || "";
};

const getClosedDays = (shop) => {
  return shop.closedDays || shop.regularHoliday || shop.holiday || "";
};

const getGenre = (shop) => {
  return shop.genre || shop.type || "";
};

const hasRow = (rows, label) => {
  return rows.some(([rowLabel]) => rowLabel === label);
};

const getFacilities = (shop) => {
  return Array.isArray(shop.facilities) ? shop.facilities : [];
};

const getLanguages = (shop) => {
  return Array.isArray(shop.languages) ? shop.languages : [];
};

export function ShopArticleTemplate({ article }) {
  const { lang } = useLanguage();
  const points = Array.isArray(article.points) ? article.points : [];
  const featuredShopNames = Array.isArray(article.featuredShopNames)
    ? article.featuredShopNames
    : [];

  return (
    <div className={styles.pointList}>
      {points.map((point, index) => {
        const pointShops = Array.isArray(point.shops) ? point.shops : [];
        const shops =
          featuredShopNames.length > 0
            ? pointShops.filter((shop) => featuredShopNames.includes(shop.name))
            : pointShops;
        const hashtags = Array.isArray(point.hashtags) ? point.hashtags : [];

        if (shops.length === 0) return null;

        return (
          <section
            key={point.number || index}
            id={getSectionId(index)}
            className={styles.pointGroup}
          >
            <div className={styles.pointGroupHeader}>
              <div>
                <div className={styles.pointHeading}>
                  <span>{point.number}</span>
                  <h3>{point.title}</h3>
                  {point.area && <small>{point.area}</small>}
                </div>

                {point.text && <p>{point.text}</p>}

                {hashtags.length > 0 && (
                  <div className={styles.hashtags}>
                    {hashtags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.shopGrid}>
              {shops.map((shop) => (
                <ShopCard
                  key={shop.name}
                  shop={shop}
                  article={article}
                  lang={lang}
                  anchorId={getShopAnchorId(index, pointShops.indexOf(shop))}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function ShopCard({ shop, article, lang, anchorId, stackedMedia }) {
  const t = blogText[lang] || blogText.JP;
  const rows = Array.isArray(shop.rows) ? shop.rows : [];
  const hashtags = Array.isArray(shop.hashtags) ? shop.hashtags : [];
  const images = Array.isArray(shop.images) ? shop.images : [];
  const instagramEmbedUrl =
    getInstagramUrl(shop.instagramEmbedUrl) ||
    getInstagramUrl(shop.instagramPostUrl) ||
    getInstagramUrl(shop.instagramUrl) ||
    images.find((image) => getInstagramUrl(image)) ||
    "";
  const hasInstagramEmbed = Boolean(instagramEmbedUrl);
  const useSplitLayout = hasInstagramEmbed || article.mediaLayout === "split";
  const displayImages = hasInstagramEmbed
    ? [instagramEmbedUrl]
    : useSplitLayout
      ? images.slice(0, 1).length > 0
        ? images.slice(0, 1)
        : [article.mainImage]
      : images.slice(0, 3).length > 0
        ? images.slice(0, 3)
        : [article.mainImage];

  const baeScore = getScore(shop.baeScore || shop.baeLevel || shop.bae);
  const shouldShowBaeScore = !article.hideBaeScore;
  const genre = getGenre(shop);
  const recommendedMenu = getRecommendedMenu(shop);
  const businessHours = getBusinessHours(shop);
  const closedDays = getClosedDays(shop);
  const mapEmbedUrl = getMapEmbedUrl(shop);
  const facilities = getFacilities(shop);
  const languages = getLanguages(shop);
  const nearestAirport = shop.nearestAirport || "";
  const labels = {
    genre: getLocalizedValue("ジャンル", lang),
    baeScore: getLocalizedValue("映え度", lang),
    recommendedMenu: getLocalizedValue("おすすめメニュー", lang),
    businessHours: getLocalizedValue("営業時間", lang),
    closedDays: getLocalizedValue("定休日", lang),
    nearestAirport: getLocalizedValue("最寄り空港", lang),
    languages: getLocalizedValue("対応言語", lang),
    facilities: getLocalizedValue("設備・サービス", lang),
  };

  const displayRows = [
    ...(genre && !hasRow(rows, labels.genre) ? [[labels.genre, genre]] : []),
    ...rows,
    ...(shouldShowBaeScore && baeScore > 0 && !hasRow(rows, labels.baeScore)
      ? [[labels.baeScore, getStars(baeScore)]]
      : []),
    ...(recommendedMenu && !hasRow(rows, labels.recommendedMenu)
      ? [[labels.recommendedMenu, recommendedMenu]]
      : []),
    ...(businessHours && !hasRow(rows, labels.businessHours)
      ? [[labels.businessHours, businessHours]]
      : []),
    ...(closedDays && !hasRow(rows, labels.closedDays)
      ? [[labels.closedDays, closedDays]]
      : []),
    ...(nearestAirport && !hasRow(rows, labels.nearestAirport)
      ? [[labels.nearestAirport, nearestAirport]]
      : []),
    ...(languages.length > 0 && !hasRow(rows, labels.languages)
      ? [[labels.languages, languages.join(" / ")]]
      : []),
  ];

  return (
    <article
      id={anchorId}
      className={`${styles.shopCard} ${
        hasInstagramEmbed ? styles.instagramLayoutCard : ""
      } ${useSplitLayout ? styles.splitLayoutCard : ""} ${
        stackedMedia ? styles.stackedMediaCard : ""
      }`}
    >
      {useSplitLayout && (
        <div className={styles.mobileShopSummary}>
          <div className={styles.shopHeading}>
            <h4>{shop.name}</h4>
            {shop.area && <small>{shop.area}</small>}
          </div>
          {shop.text && <p>{shop.text}</p>}
        </div>
      )}

      <div
        className={`${styles.shopImages} ${useSplitLayout ? styles.instagramMediaGrid : ""}`}
      >
        {displayImages.length > 0 &&
          displayImages.map((image, index) => (
            <BlogMedia
              key={`${shop.name}-${index}`}
              media={image}
              alt={`${shop.name} ${t.articlePhotoAlt} ${index + 1}`}
            />
          ))}
      </div>

      <div className={styles.shopContent}>
        <div className={styles.shopHeading}>
          <h4>{shop.name}</h4>
          {shop.area && <small>{shop.area}</small>}
        </div>

        {shop.text && <p>{shop.text}</p>}

        {displayRows.length > 0 && (
          <dl>
            {displayRows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd
                  className={label === labels.baeScore ? styles.starText : ""}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {facilities.length > 0 && (
          <div className={styles.facilitiesBlock}>
            <span className={styles.facilitiesLabel}>{labels.facilities}</span>
            <div className={styles.facilityTags}>
              {facilities.map((facility) => (
                <span key={facility}>
                  <i aria-hidden="true">{getFacilityIcon(facility)}</i>
                  {facility}
                </span>
              ))}
            </div>
          </div>
        )}

        {hashtags.length > 0 && (
          <div className={styles.hashtags}>
            {hashtags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}

        <div className={styles.shopMapPanel}>
          <div className={styles.shopMapHeader}>
            <div>
              <span>LOCATION</span>
              <strong>{shop.name}</strong>
              {shop.area && <p>{shop.area}</p>}
            </div>
            {shop.mapUrl && (
              <a
                href={shop.mapUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackEvent("map_navigate_click", { spot_name: shop.name })
                }
              >
                {t.googleMapOpen}
              </a>
            )}
          </div>

          <div className={styles.shopMapFrame}>
            <iframe
              title={`${shop.name} Google Map`}
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        {shop.instagramUrl && (
          <div className={styles.shopLinks}>
            <a
              href={shop.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.shopInstagramLink}
              onClick={() =>
                trackEvent("instagram_click", { spot_name: shop.name })
              }
            >
              <span>{shop.instagramLabel || "Instagram"}</span>
            </a>
          </div>
        )}

        {shop.websiteUrl && (
          <div className={styles.shopLinks}>
            <a
              href={shop.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.shopWebsiteLink}
              onClick={() =>
                trackEvent("booking_link_click", {
                  spot_name: shop.name,
                  label: shop.websiteLabel,
                })
              }
            >
              <span>{shop.websiteLabel || t.websiteDetail}</span>
            </a>
          </div>
        )}
      </div>
    </article>
  );
}
